import { useState } from 'react';
import { message } from 'antd';
import { useSimpleStore } from '../stores/simpleStore';

interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

interface UseApiCallResult<T> {
  loading: boolean;
  error: ApiError | null;
  execute: (apiCall: () => Promise<T>) => Promise<T | null>;
  clearError: () => void;
}

/**
 * API 호출을 위한 통합 에러 처리 훅
 * 모든 컴포넌트에서 일관된 에러 처리와 로딩 상태 관리를 제공합니다.
 */
export function useApiCall<T = any>(): UseApiCallResult<T> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const execute = async (apiCall: () => Promise<T>): Promise<T | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiCall();
      return result;
      
    } catch (err) {
      console.error('API 호출 에러:', err);
      
      let apiError: ApiError;
      
      if (err instanceof Error) {
        if (err.message.includes('fetch')) {
          // 네트워크 에러
          apiError = {
            message: '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.',
            status: 0,
            code: 'NETWORK_ERROR'
          };
        } else {
          apiError = {
            message: err.message,
            code: 'API_ERROR'
          };
        }
      } else if (typeof err === 'object' && err !== null) {
        // 구조화된 에러 객체
        const errorObj = err as any;
        apiError = {
          message: errorObj.message || errorObj.error || '알 수 없는 오류가 발생했습니다.',
          status: errorObj.status || errorObj.statusCode,
          code: errorObj.code || errorObj.error_code
        };
      } else {
        apiError = {
          message: '알 수 없는 오류가 발생했습니다.',
          code: 'UNKNOWN_ERROR'
        };
      }
      
      setError(apiError);
      
      // 사용자에게 토스트 메시지 표시 및 특수 처리
      if (apiError.status === 401) {
        // 메시지 없이 바로 로그아웃 처리
        const { logout } = useSimpleStore.getState();
        logout();
        // 페이지 새로고침으로 로그인 페이지로 리디렉트
        window.location.reload();
      } else if (apiError.status === 403) {
        message.error('권한이 없습니다.');
      } else if (apiError.status === 404) {
        message.error('요청한 리소스를 찾을 수 없습니다.');
      } else if (apiError.status === 500) {
        message.error('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      } else {
        message.error(apiError.message);
      }
      
      return null;
      
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    loading,
    error,
    execute,
    clearError
  };
}

/**
 * API 응답 형식을 표준화하는 유틸리티 함수
 */
export function handleApiResponse<T>(response: any): T {
  if (!response) {
    throw new Error('응답이 비어있습니다.');
  }

  if (response.success === false) {
    throw new Error(response.error || response.message || 'API 요청이 실패했습니다.');
  }

  if (response.success === true) {
    return response.data;
  }

  // 기본적으로 response 자체를 반환 (기존 코드와의 호환성)
  return response;
}